import React from 'react';
import styled from 'styled-components';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useImportStationTextStore } from '../../shared/store/importStationTextStore';
import { useProjectDetailState } from '../../shared/hooks/useApi/useProjectDetail';
import { useCanShowProjectDetail } from '../../shared/hooks/useProjectVisibility';
import { useProjectElementActions } from '../../shared/hooks/useApi/useProjectElement';
import { peTextImportBuilder } from '../../shared/converter/entityBuilder';
import { TextBlockData } from '../../shared/dto/EntityRepository';
import SimpleTextBlock from './SimpleTextBlock';
import { getModalMessage } from '../../shared/constants/modalMessages';
import { getPageMessage } from '../../shared/constants/pageMessages';
import HeadlessBtn from '../../shared/component/headless/button/HeadlessBtn';
import { BlackFitBtn, PaperBlurFitBtn } from '../../shared/component/headless/button/BtnBody';
import { getBtnName } from '../../shared/constants/btnNames';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { AlertPosition, AlertType } from '../../shared/enum/EnumRepository';

const ImportStationTextSub: React.FC = () => {
  const { clearModal } = useModalStore();
  const { selectedId, simpleSubList, selectedSubIdList, clearSimpleList } = useImportStationTextStore();
  const { project } = useProjectDetailState();
  const canShowProject = useCanShowProjectDetail();
  const { createProjectElementWithText } = useProjectElementActions();
  const { enqueueAlert } = useStandardAlertStore();

  const handleSubmit = async () => {
    if (!project) return;
    if (!selectedId || selectedSubIdList.length === 0) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('SELECT_TO_IMPORT_EMPTY'),
      });
      return;
    }
    await createProjectElementWithText(
      peTextImportBuilder(project.id, selectedId, selectedSubIdList),
    );
  };

  const handleCancel = () => {
    clearSimpleList();
    clearModal(400);
  };

  if (!canShowProject || !project) return null;

  return (
    <>
      {selectedId && (
        <SubContainer $isActive={selectedId !== ''}>
          <ImportTitleWrapper>
            <ImportTitle>{getModalMessage('TEXT_BLOCKS')}</ImportTitle>
          </ImportTitleWrapper>
          <ImportContentContainer>
            {simpleSubList.length === 0 ? (
              <NoContentContainer>
                {getPageMessage('NO_DATA_FOUND')} <br />
                {getPageMessage('ADD_FIRST', { type: getModalMessage('TEXT_BLOCKS') })}
              </NoContentContainer>
            ) : (
              simpleSubList.map((sw) => (
                <SimpleContentComp key={sw.id}>
                  <SimpleTextBlock
                    data={sw as TextBlockData}
                    selectModeForNewElement
                  />
                </SimpleContentComp>
              ))
            )}
          </ImportContentContainer>
          <ButtonContainer>
            <HeadlessBtn
              value={getBtnName('IMPORT')}
              handleClick={handleSubmit}
              StyledBtn={BlackFitBtn}
            />
            <HeadlessBtn
              value={getBtnName('CANCEL')}
              handleClick={handleCancel}
              StyledBtn={PaperBlurFitBtn}
            />
          </ButtonContainer>
        </SubContainer>
      )}
    </>
  );
};

const SubContainer = styled.div<{ $isActive: boolean }>`
  width: ${({ $isActive }) => ($isActive ? '36vw' : '0')};
  min-width: ${({ $isActive }) => ($isActive ? '380px' : '0')};
  max-width: ${({ $isActive }) => ($isActive ? '380px' : '0')};
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1.5vw;
  transition: all 0.6s ease-out;
`;

const ImportTitleWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5vh;
`;

const ImportTitle = styled.div`
  display: flex;
  justify-content: flex-start;
  ${({ theme }) => theme.typography.Body_02_1};
`;

const ImportContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1vw;
  overflow-y: scroll;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const NoContentContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_2};
`;

const SimpleContentComp = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

export default ImportStationTextSub;
